import React, { useState, useRef } from 'react';
import { useLang } from '../context/LanguageContext';
import PageHero from '../components/PageHero';
import CTASection from '../components/CTASection';
import FormShell, { Field, inputCls } from '../components/FormShell';
import { submitToSheet, generateSubmissionId, getVisitorId, sanitizePhone } from '../lib/api';
import { MapPin, Briefcase } from 'lucide-react';

const Jobs = () => {
  const { t, lang } = useLang();
  const [openJob, setOpenJob] = useState(null);
  const initial = { full_name: '', email: '', phone: '', country: '', job_title: '', experience: '', resume: '', additional_notes: '', consent: false };
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [locked, setLocked] = useState(false);
  const [success, setSuccess] = useState(false);
  const [refNo, setRefNo] = useState('');
  const submitLock = useRef(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent || submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    setLocked(true);
    const submission_id = generateSubmissionId('SIB-JOB');
    const payload = {
      form_type: 'job_application',
      submission_id,
      visitor_id: getVisitorId(),
      full_name: form.full_name,
      email: form.email,
      phone: form.phone,
      country: form.country,
      job_title: form.job_title,
      experience: form.experience,
      resume: form.resume,
      additional_notes: form.additional_notes,
      language: lang,
      page_source: 'jobs',
      status: 'submitted'
    };
    await submitToSheet(payload);
    setRefNo(submission_id);
    setSubmitting(false);
    setSuccess(true);
    setTimeout(() => { submitLock.current = false; setLocked(false); }, 4000);
  };
  const handleReset = () => { setSuccess(false); setRefNo(''); setForm({ ...initial, job_title: openJob?.title || '' }); submitLock.current = false; setLocked(false); };

  return (
    <div>
      <PageHero title={t.jobs.title} desc={t.jobs.desc} />
      <section className="bg-[#FBF9F2] py-20">
        <div className="sib-container">
          <h2 className="section-title text-center mb-10">{t.jobs.openRoles}</h2>
          <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            {t.jobs.list.map((j, i) => (
              <div key={i} className="bg-white border border-[#E8E1CE] rounded-lg p-6 card-hover">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-11 h-11 rounded-md bg-[#0B5D3B]/10 flex items-center justify-center flex-shrink-0"><Briefcase className="w-5 h-5 text-[#0B5D3B]" /></div>
                  <div>
                    <h3 className="font-semibold text-[#0B5D3B] text-[16px]">{j.title}</h3>
                    <div className="flex flex-wrap gap-3 text-xs text-[#33473d]/70 mt-1">
                      <span className="inline-flex items-center gap-1"><MapPin size={12} />{j.location}</span>
                      <span>{j.type}</span>
                    </div>
                  </div>
                </div>
                <p className="text-[13.5px] text-[#33473d]/85 leading-relaxed mb-4">{j.desc}</p>
                <button onClick={() => { setOpenJob(j); setForm({ ...initial, job_title: j.title }); setSuccess(false); setRefNo(''); }} className="btn-green text-sm">{t.forms.applyNow}</button>
              </div>
            ))}
          </div>
        </div>
      </section>
      {openJob && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setOpenJob(null)}>
          <div onClick={e => e.stopPropagation()}>
            <FormShell title={`${t.forms.applyNow}: ${openJob.title}`} onSubmit={handleSubmit} submitting={submitting} locked={locked} success={success} onReset={handleReset} close={() => setOpenJob(null)} refNo={refNo}>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label={t.forms.fullName} required><input required className={inputCls} value={form.full_name} onChange={e => set('full_name', e.target.value)} /></Field>
                <Field label={t.forms.email} required><input required type="email" className={inputCls} dir="ltr" value={form.email} onChange={e => set('email', e.target.value)} /></Field>
                <Field label={t.forms.phone} required>
                  <input
                    required
                    type="tel"
                    inputMode="tel"
                    pattern="[0-9+\s\-()]{6,20}"
                    title={t.forms.phoneHelp}
                    className={inputCls}
                    dir="ltr"
                    value={form.phone}
                    onChange={e => set('phone', sanitizePhone(e.target.value))}
                    onKeyDown={(e) => { const allowed = /[0-9+\s\-()]/; if (e.key.length === 1 && !allowed.test(e.key) && !e.ctrlKey && !e.metaKey) e.preventDefault(); }}
                  />
                </Field>
                <Field label={t.forms.country} required><select required className={inputCls} value={form.country} onChange={e => set('country', e.target.value)}><option value="">{t.forms.selectCountry}</option>{t.forms.countries.map(c => <option key={c}>{c}</option>)}</select></Field>
                <Field label={t.forms.experience} required><input required className={inputCls} value={form.experience} onChange={e => set('experience', e.target.value)} /></Field>
                <Field label={t.forms.resume} required><input required className={inputCls} dir="ltr" placeholder="https://" value={form.resume} onChange={e => set('resume', e.target.value)} /></Field>
              </div>
              <Field label={t.forms.additionalNotes}><textarea className={inputCls} rows={3} value={form.additional_notes} onChange={e => set('additional_notes', e.target.value)} /></Field>
              <label className="flex items-start gap-2 text-sm text-[#33473d]"><input type="checkbox" required checked={form.consent} onChange={e => set('consent', e.target.checked)} className="mt-1" /><span>{t.forms.consent}</span></label>
            </FormShell>
          </div>
        </div>
      )}
      <CTASection />
    </div>
  );
};
export default Jobs;
